/**
 * Properties panel for the selected station / zone / flow.
 * Edits dispatch straight into the draft (each field commit = one undo step).
 */
import { useTranslation } from 'react-i18next';
import {
  Box,
  Field,
  HStack,
  Input,
  Switch,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { Button as ButtonComponent, SelectInput } from '@themes/aegix';
import { ColorPickerInput } from '@themes/aegix';
import {
  FLOW_TYPE_OPTIONS,
  stationTypeOption,
  zoneTypeOption,
} from './constants';
import type { DraftAction } from './useFloorplanDraft';
import type {
  DraftFlow,
  DraftStation,
  DraftZone,
  FloorplanDraft,
  Selection,
} from './types';

interface PropertiesPanelProps {
  draft: FloorplanDraft;
  selection: Selection | null;
  dispatch: (action: DraftAction) => void;
  onDeselect: () => void;
}

const toInt = (value: string, fallback: number) => {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? fallback : Math.max(0, parsed);
};

function NumberField({
  label,
  value,
  onCommit,
  e2e,
}: {
  label: string;
  value: number | null;
  onCommit: (value: number | null) => void;
  e2e: string;
}) {
  return (
    <Field.Root>
      <Field.Label>{label}</Field.Label>
      <Input
        size="sm"
        type="number"
        min={0}
        defaultValue={value ?? ''}
        key={`${e2e}-${value ?? 'null'}`}
        onBlur={(e) => {
          const raw = e.target.value.trim();
          onCommit(raw === '' ? null : toInt(raw, 0));
        }}
        data-e2e={e2e}
      />
    </Field.Root>
  );
}

function TextField({
  label,
  value,
  onCommit,
  e2e,
  textarea = false,
}: {
  label: string;
  value: string;
  onCommit: (value: string) => void;
  e2e: string;
  textarea?: boolean;
}) {
  const shared = {
    size: 'sm' as const,
    defaultValue: value,
    key: `${e2e}-${value}`,
    onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (e.target.value !== value) onCommit(e.target.value);
    },
    'data-e2e': e2e,
  };
  return (
    <Field.Root>
      <Field.Label>{label}</Field.Label>
      {textarea ? <Textarea {...shared} rows={2} /> : <Input {...shared} />}
    </Field.Root>
  );
}

function ToggleField({
  label,
  checked,
  onCommit,
  e2e,
}: {
  label: string;
  checked: boolean;
  onCommit: (checked: boolean) => void;
  e2e: string;
}) {
  return (
    <Switch.Root
      size="sm"
      checked={checked}
      onCheckedChange={(d) => onCommit(!!d.checked)}
      data-e2e={e2e}
    >
      <Switch.HiddenInput />
      <Switch.Control />
      <Switch.Label>{label}</Switch.Label>
    </Switch.Root>
  );
}

function StationProperties({
  station,
  dispatch,
  onDeselect,
}: {
  station: DraftStation;
  dispatch: (action: DraftAction) => void;
  onDeselect: () => void;
}) {
  const { t } = useTranslation();
  const patch = (fields: Partial<DraftStation>) =>
    dispatch({ type: 'update_station', cid: station.cid, patch: fields });
  const opt = stationTypeOption(station.station_type);
  const typeLabel = t(`reunification.stationType.${opt.value}`, opt.label);

  return (
    <VStack gap={3} align="stretch">
      <Text fontWeight={600}>
        {t('reunification.stationHeader', 'Station — {{label}}', { label: typeLabel })}
      </Text>
      <TextField
        label={t('common.name', 'Name')}
        value={station.name}
        onCommit={(name) => patch({ name })}
        e2e="station-prop-name"
      />
      <TextField
        label={t('common.description', 'Description')}
        value={station.description}
        onCommit={(description) => patch({ description })}
        e2e="station-prop-description"
        textarea
      />
      <HStack gap={3} align="start">
        <NumberField
          label={t('reunification.minStaff', 'Min Staff')}
          value={station.min_staff}
          onCommit={(v) => patch({ min_staff: v ?? 0 })}
          e2e="station-prop-min-staff"
        />
        <NumberField
          label={t('reunification.recommendedStaff', 'Recommended')}
          value={station.recommended_staff}
          onCommit={(v) => patch({ recommended_staff: v ?? 0 })}
          e2e="station-prop-recommended-staff"
        />
      </HStack>
      <HStack gap={3} align="start">
        <NumberField
          label={t('reunification.staffPerStudents', 'Staff per N students')}
          value={station.staff_ratio_per_students}
          onCommit={(v) => patch({ staff_ratio_per_students: v })}
          e2e="station-prop-staff-ratio"
        />
        <NumberField
          label={t('reunification.capacityUnlimited', 'Capacity (0 = unlimited)')}
          value={station.capacity}
          onCommit={(v) => patch({ capacity: v ?? 0 })}
          e2e="station-prop-capacity"
        />
      </HStack>
      <TextField
        label={t('reunification.requiredEquipment', 'Required equipment (comma-separated)')}
        value={station.requires_equipment.join(', ')}
        onCommit={(raw) =>
          patch({
            requires_equipment: raw
              .split(',')
              .map((item) => item.trim())
              .filter(Boolean),
          })
        }
        e2e="station-prop-equipment"
      />
      <ToggleField
        label={t('reunification.requiredForPlanValidity', 'Required for plan validity')}
        checked={station.is_required}
        onCommit={(is_required) => patch({ is_required })}
        e2e="station-prop-required"
      />
      <ButtonComponent
        visual="alert"
        size="sm"
        onClick={() => {
          dispatch({ type: 'remove_station', cid: station.cid });
          onDeselect();
        }}
        data-e2e="station-prop-delete"
      >
        {t('reunification.deleteStation', 'Delete Station')}
      </ButtonComponent>
    </VStack>
  );
}

function ZoneProperties({
  zone,
  dispatch,
  onDeselect,
}: {
  zone: DraftZone;
  dispatch: (action: DraftAction) => void;
  onDeselect: () => void;
}) {
  const { t } = useTranslation();
  const patch = (fields: Partial<DraftZone>) =>
    dispatch({ type: 'update_zone', cid: zone.cid, patch: fields });
  const opt = zoneTypeOption(zone.zone_type);
  const typeLabel = t(`reunification.zoneType.${opt.value}`, opt.label);

  return (
    <VStack gap={3} align="stretch">
      <Text fontWeight={600}>
        {t('reunification.zoneHeader', 'Zone — {{label}}', { label: typeLabel })}
      </Text>
      <TextField
        label={t('common.name', 'Name')}
        value={zone.name}
        onCommit={(name) => patch({ name })}
        e2e="zone-prop-name"
      />
      <TextField
        label={t('common.description', 'Description')}
        value={zone.description}
        onCommit={(description) => patch({ description })}
        e2e="zone-prop-description"
        textarea
      />
      <NumberField
        label={t('reunification.capacityUnlimited', 'Capacity (0 = unlimited)')}
        value={zone.capacity}
        onCommit={(v) => patch({ capacity: v ?? 0 })}
        e2e="zone-prop-capacity"
      />
      <Field.Root>
        <Field.Label>{t('reunification.color', 'Color')}</Field.Label>
        <ColorPickerInput
          value={zone.color}
          onChange={(color) => patch({ color })}
        />
      </Field.Root>
      <ButtonComponent
        visual="alert"
        size="sm"
        onClick={() => {
          dispatch({ type: 'remove_zone', cid: zone.cid });
          onDeselect();
        }}
        data-e2e="zone-prop-delete"
      >
        {t('reunification.deleteZone', 'Delete Zone')}
      </ButtonComponent>
    </VStack>
  );
}

function endpointLabel(
  draft: FloorplanDraft,
  flow: DraftFlow,
  side: 'from' | 'to',
  t: (key: string, defaultValue: string) => string,
): string {
  const endpoint = flow[side];
  if (endpoint.kind === 'station') {
    return (
      draft.stations.find((s) => s.cid === endpoint.cid)?.name ??
      t('reunification.stationFallback', 'Station')
    );
  }
  return (
    draft.zones.find((z) => z.cid === endpoint.cid)?.name ??
    t('reunification.zoneFallback', 'Zone')
  );
}

function FlowProperties({
  draft,
  flow,
  dispatch,
  onDeselect,
}: {
  draft: FloorplanDraft;
  flow: DraftFlow;
  dispatch: (action: DraftAction) => void;
  onDeselect: () => void;
}) {
  const { t } = useTranslation();
  const patch = (fields: Partial<DraftFlow>) =>
    dispatch({ type: 'update_flow', cid: flow.cid, patch: fields });
  return (
    <VStack gap={3} align="stretch">
      <Text fontWeight={600}>
        {t('reunification.flowHeader', 'Flow — {{from}} → {{to}}', {
          from: endpointLabel(draft, flow, 'from', t),
          to: endpointLabel(draft, flow, 'to', t),
        })}
      </Text>
      <Field.Root>
        <Field.Label>{t('reunification.flowTypeLabel', 'Flow type')}</Field.Label>
        <SelectInput
          value={flow.flow_type}
          onChange={(flow_type) => patch({ flow_type })}
          option={FLOW_TYPE_OPTIONS.map(({ value, label }) => ({
            value,
            label: t(`reunification.flowType.${value}`, label),
          }))}
        />
      </Field.Root>
      <TextField
        label={t('common.description', 'Description')}
        value={flow.description}
        onCommit={(description) => patch({ description })}
        e2e="flow-prop-description"
        textarea
      />
      <ToggleField
        label={t('reunification.primaryPath', 'Primary path (off = alternate)')}
        checked={flow.is_primary_path}
        onCommit={(is_primary_path) => patch({ is_primary_path })}
        e2e="flow-prop-primary"
      />
      <ButtonComponent
        visual="alert"
        size="sm"
        onClick={() => {
          dispatch({ type: 'remove_flow', cid: flow.cid });
          onDeselect();
        }}
        data-e2e="flow-prop-delete"
      >
        {t('reunification.deleteFlow', 'Delete Flow')}
      </ButtonComponent>
    </VStack>
  );
}

export function PropertiesPanel({
  draft,
  selection,
  dispatch,
  onDeselect,
}: PropertiesPanelProps) {
  const { t } = useTranslation();
  let content: React.ReactNode = (
    <Text fontSize="sm" color="text.secondary">
      {t(
        'reunification.propertiesPanelEmpty',
        'Select a station, zone, or flow to edit its properties. Use the left palette to place new elements.',
      )}
    </Text>
  );
  if (selection?.kind === 'station') {
    const station = draft.stations.find((s) => s.cid === selection.cid);
    if (station) {
      content = (
        <StationProperties
          station={station}
          dispatch={dispatch}
          onDeselect={onDeselect}
        />
      );
    }
  } else if (selection?.kind === 'zone') {
    const zone = draft.zones.find((z) => z.cid === selection.cid);
    if (zone) {
      content = (
        <ZoneProperties
          zone={zone}
          dispatch={dispatch}
          onDeselect={onDeselect}
        />
      );
    }
  } else if (selection?.kind === 'flow') {
    const flow = draft.flows.find((f) => f.cid === selection.cid);
    if (flow) {
      content = (
        <FlowProperties
          draft={draft}
          flow={flow}
          dispatch={dispatch}
          onDeselect={onDeselect}
        />
      );
    }
  }
  return (
    <Box
      w="18rem"
      flexShrink={0}
      borderLeftWidth="1px"
      borderColor="border"
      p={4}
      overflowY="auto"
      data-e2e="floorplan-editor-properties"
    >
      {content}
    </Box>
  );
}
